import React, { Fragment, memo } from 'react'
import tw from 'twin.macro'
import { Button } from 'src/components/shared/Button'
import { CommonModal } from 'src/components/shared/CommonModal'
import type { DocusignTemplate } from 'src/interfaces/docusignTemplate'
import type { InsurancePolicyProduct } from 'src/interfaces/insurancePolicy'
import type { InsuranceProduct } from 'src/interfaces/insuranceProduct'
import { InsurancePolicyProductForm } from './InsurancePolicyProductForm'
import type {
  InsurancePolicyProductFormMode,
  InsurancePolicyProductFormValues,
} from './InsurancePolicyProductForm.interface'

const FORM_ID = 'insurance-policy-product-form'

interface Props {
  isOpen: boolean
  mode: InsurancePolicyProductFormMode
  policyProduct: InsurancePolicyProduct | null
  selectableProducts: InsuranceProduct[]
  docusignTemplates: DocusignTemplate[]
  isSaving: boolean
  onSubmit(values: InsurancePolicyProductFormValues): void
  onClose(): void
}

const InsurancePolicyProductFormModalComponent = ({
  isOpen,
  mode,
  policyProduct,
  selectableProducts,
  docusignTemplates,
  isSaving,
  onSubmit,
  onClose,
}: Props) => {
  return (
    <CommonModal
      isOpen={isOpen}
      title={mode === 'edit' ? 'Edit policy product' : 'Add product to policy'}
      // Six sections of fields — wider than the policy modal, and the modal's own body scrolls.
      className={tw`max-w-[640px]`}
      onClose={onClose}
      buttons={
        <Fragment>
          <Button size="small" color="grey" disabled={isSaving} onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="small"
            color="primary"
            type="submit"
            form={FORM_ID}
            loading={isSaving}
            disabled={isSaving}
            data-testid="insurance-policy-product-form-submit-button"
          >
            {mode === 'edit' ? 'Save changes' : 'Add product'}
          </Button>
        </Fragment>
      }
    >
      <InsurancePolicyProductForm
        formId={FORM_ID}
        mode={mode}
        policyProduct={policyProduct}
        selectableProducts={selectableProducts}
        docusignTemplates={docusignTemplates}
        onSubmit={onSubmit}
      />
    </CommonModal>
  )
}

export const InsurancePolicyProductFormModal = memo(InsurancePolicyProductFormModalComponent)
